import { AppBox, AppGridBox, AppLink, AppText } from "../../../../../commons/components";
import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { AxiosError } from "axios";
import { useLayoutEffect, useState } from "react";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import AppBorderBox from "../../../../../commons/components/BorderBox";
import UserBasicInfoAddEdit from "./UserBasicInfoAddEdit";
import { cleanUpStepperFormData, setStepperFormData } from "../../../../../redux/slices/stepper-form-slice";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { User } from "../../../../../commons/models/user";
import { IUserDTO } from "../../../../../api-services/manage-user-service";
import { userEdit } from "../../../../../redux/slices/user/manage-user-edit-slice";
import { userAdd } from "../../../../../redux/slices/user/manage-user-add-slice";
import { cleanUpToolbarSearchTitle } from "../../../../../redux/slices/toolbar-search-slice";
import { getErrorKeyPosition } from "../../../../../commons/utilities/form-utils";
import { useNavigate } from "react-router";
import useAuth from "../../../../../hooks/useAuth";
import { authUserProfile } from "../../../../../redux/slices/profile-slice";
import ability from "../../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import FormImageAddEdit from "../../../FormImageAddEdit";
import isNullOrUndefined from "../../../../../commons/utilities/null-or-undefined";

interface UserAddEditProps {
  userData?: User | null;
  onCancel?: () => void;
  onSave: (data?: User) => void;
}

const stepperFormOptions = [
  {
    step: 1,
    title: "User's Image",
  },
  {
    step: 2,
    title: "Basic Information",
  },
];

const stepperFromKeyMap = [["avatar"], ["firstName", "middleName", "lastName", "email"]];

export default function UserAddEdit({ userData, onCancel, onSave }: UserAddEditProps) {
  const methods = useForm();
  const formErrors = methods.formState.errors;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const addSnackbar = useSnackbar();
  const { user: authUser } = useAuth();
  const [selectedStepperFormOption, setSelectedStepperFormOption] = useState(stepperFormOptions[0]);
  const formData = useAppSelector<IUserDTO>((state) => state.stepperForm.stepperFormData);
  const { userAddResponseStatus } = useAppSelector((state) => state.manageUserAdd);
  const { userEditResponseStatus } = useAppSelector((state) => state.manageUserEdit);
  const { uploadedImageStatus } = useAppSelector((state) => state.imageUpload);

  //initiate the stepperData
  useLayoutEffect(() => {
    if (!userData) {
      const userData: IUserDTO = {
        firstName: "",
        lastName: "",
        middleName: "",
        email: "",
        avatar: null,
      };
      dispatch(setStepperFormData<IUserDTO>(userData));
    }
  }, []);

  useLayoutEffect(() => {
    if (userData) {
      const userDTO: IUserDTO = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        middleName: userData.middleName,
        email: userData.email,
        avatar: userData.avatar,
      };
      dispatch(setStepperFormData<IUserDTO>(userDTO));
    }
  }, [userData]);

  const handleNext = (currentStep: number) => {
    if (
      userAddResponseStatus !== StatusEnum.Loading &&
      userEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep]);
    }
  };
  const handleBack = (currentStep: number) => {
    if (
      userAddResponseStatus !== StatusEnum.Loading &&
      userEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep - 2]);
    }
  };

  const handleStepperOptionClick = (option: any) => {
    if (
      userAddResponseStatus !== StatusEnum.Loading &&
      userEditResponseStatus !== StatusEnum.Loading &&
      uploadedImageStatus !== StatusEnum.Loading
    ) {
      setSelectedStepperFormOption(option);
    }
  };

  const handleFormSubmit = () => {
    const formattedValues: IUserDTO = {
      ...formData,
      avatar: formData.avatar ? formData.avatar : null,
    };
    dispatch(
      userData
        ? userEdit({
            userId: userData.id,
            userData: formattedValues,
          })
        : userAdd({ userData: formattedValues })
    )
      .unwrap()
      .then(async (response) => {
        if (userData) {
          dispatch(cleanUpToolbarSearchTitle());
          addSnackbar({
            key: "user-edit-success",
            text: "User Edited Successfully",
            variant: "success",
          });
          const isCurrentUser = userData.id === authUser?.id;
          if (isCurrentUser) {
            await dispatch(authUserProfile()).unwrap();
            if (ability.cannot(RolePolicyActionEnum.READ, User)) {
              navigate("/manage");
              return;
            }
          }
        } else {
          addSnackbar({
            key: "user-add-success",
            text: "User Added Successfully",
            variant: "success",
          });
        }
        dispatch(cleanUpStepperFormData());
        onSave(response);
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        const responseData = error.response?.data;
        if (error.response?.status === 417) {
          addSnackbar({
            key: "error",
            text: "Form not valid",
            variant: "danger",
          });
          if (responseData) {
            const stepperPositionSet = new Set<number>();
            Object.entries(responseData).forEach(([field, messages]) => {
              messages.forEach((message: string) => {
                methods.setError(field, { message });
                const stepperPosition = getErrorKeyPosition(field, stepperFromKeyMap);
                if (stepperPosition) {
                  stepperPositionSet.add(stepperPosition.step);
                }
                const lowestStep = Math.min(...Array.from(stepperPositionSet));
                setSelectedStepperFormOption(stepperFormOptions[lowestStep]);
              });
            });
          }
        } else {
          addSnackbar({
            key: "error",
            text: responseData?.message,
            variant: "danger",
          });
        }
      });
  };

  const clearErrorField = (key: string) => {
    methods.clearErrors(key);
  };

  return (
    <FormProvider {...methods}>
      <AppBox flexDirection="column" gap="sm" style={{ height: "var(--modal-md)" }}>
        <AppGridBox className=" flex-1" style={{ gridTemplateColumns: "1fr 3fr" }} gap="sm">
          <AppBorderBox border={["Right"]}>
            <AppBox flexDirection="column" gap="2xs" className="w-100">
              {stepperFormOptions.map((option, index) => (
                <AppLink
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    handleStepperOptionClick(option);
                  }}
                >
                  <AppBorderBox
                    border={option.step === selectedStepperFormOption.step ? ["Right"] : []}
                    borderColor="primary"
                  >
                    <AppBox pl="sm" py="2xs">
                      <AppText
                        fontWeight="semibold"
                        size="lg"
                        color={option.step === selectedStepperFormOption.step ? "primary" : "currentColor"}
                      >
                        {option.title}
                      </AppText>
                    </AppBox>
                  </AppBorderBox>
                </AppLink>
              ))}
            </AppBox>
          </AppBorderBox>
          <AppBox flexDirection="column" className="w-100 flex-1">
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 1 ? { display: "flex" } : { display: "none" }}
            >
              <FormImageAddEdit
                image={userData?.avatar}
                title="User's Image"
                DTOKey="avatar"
                isEdit={!isNullOrUndefined(userData)}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                onSkipOrNext={handleNext}
                onClearError={clearErrorField}
                submitStatus={userEditResponseStatus}
                onSaveAndExit={handleFormSubmit}
              />
            </AppBox>
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 2 ? { display: "flex" } : { display: "none" }}
            >
              <UserBasicInfoAddEdit
                userData={userData}
                formStep={selectedStepperFormOption.step}
                submitStatus={userAddResponseStatus || userEditResponseStatus}
                formErrors={formErrors}
                onBack={handleBack}
                onSubmit={handleFormSubmit}
                onClearError={clearErrorField}
              />
            </AppBox>
          </AppBox>
        </AppGridBox>
      </AppBox>
    </FormProvider>
  );
}
